'use client';

import { useEffect } from 'react';
import Link from 'next/link';
import { motion, Variants } from 'framer-motion';
import { RefreshCw, ArrowLeft, AlertTriangle } from 'lucide-react';

// Enhanced animation variants 
const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1, 
    transition: { 
      staggerChildren: 0.15,
      delayChildren: 0.1,
      duration: 0.7,
      ease: [0.22, 1, 0.36, 1]
    } 
  }
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] }
  }
};

const iconVariants: Variants = {
  hidden: { opacity: 0, scale: 0.8, rotate: -10 },
  visible: { 
    opacity: 1, 
    scale: 1,
    rotate: 0,
    transition: { 
      duration: 0.8, 
      ease: [0.34, 1.56, 0.64, 1],
      delay: 0.2
    }
  }
};

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Log the error to an error reporting service
    console.error(error);
  }, [error]);

  return (
    <div className="flex justify-center">
      <div className="w-full max-w-[1440px]">
        <motion.div 
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="min-h-screen flex items-center justify-center relative p-4"
        >
          {/* Fixed background elements */}
          <div className="absolute inset-0 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-black"></div>
          
          {/* Content container with fixed positioning */}
          <motion.div
            variants={itemVariants}
            className="w-full max-w-xl mx-auto px-6 py-10 sm:px-10 sm:py-14 premium-glass-nav dark:premium-glass-dark-nav premium-radius-xl premium-shadow-lg dark:shadow-2xl dark:shadow-primary-900/30 backdrop-blur-md text-center relative z-10"
          >
            {/* Error icon with enhanced animation */}
            <motion.div
              variants={iconVariants}
              className="flex justify-center mb-6 transform-gpu"
              whileHover={{ 
                scale: 1.05,
                rotate: [-2, 2, -2, 0],
                transition: { duration: 0.5 }
              }}
            >
              <AlertTriangle 
                size={80} 
                className="text-red-500 dark:text-red-400 drop-shadow-md dark:drop-shadow-lg"
                strokeWidth={1.5}
              />
            </motion.div>
            
            <motion.div variants={itemVariants} className="space-y-6">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 dark:text-white mb-2 sm:mb-3">
                Something went wrong!
              </h1>
              
              <p className="text-gray-600 dark:text-gray-300 mb-4 sm:mb-6 text-base sm:text-lg max-w-md mx-auto">
                We're sorry, but we encountered an error while processing your request.
                {error.digest && (
                  <span className="block mt-2 text-sm text-gray-500 dark:text-gray-400">
                    Error reference: {error.digest}
                  </span>
                )}
              </p>
              
              {/* Buttons with improved responsive layout and accessibility */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <motion.button 
                  onClick={() => reset()}
                  className="inline-flex items-center justify-center w-full sm:w-auto h-12 px-6 text-base font-medium text-white premium-gradient-primary premium-radius-button premium-shadow-md dark:shadow-lg dark:shadow-primary-900/40 premium-transition-all premium-hover-lift transform-gpu"
                  whileHover={{
                    scale: 1.05,
                    boxShadow: "0 20px 25px -5px rgba(255, 82, 82, 0.35)",
                  }}
                  transition={{ type: "spring", stiffness: 400, damping: 10 }}
                >
                  <RefreshCw size={18} className="mr-2" />
                  Try Again
                </motion.button>
                
                <Link href="/" className="w-full sm:w-auto">
                  <motion.button 
                    className="inline-flex items-center justify-center w-full sm:w-auto h-12 px-6 text-base font-medium text-primary-600 dark:text-gray-100 bg-white dark:bg-gray-800 border border-primary-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-gray-600 premium-radius-button premium-shadow-sm dark:shadow-lg dark:shadow-gray-900/40 premium-transition-all premium-hover-lift transform-gpu"
                    whileHover={{ 
                      scale: 1.05,
                      boxShadow: "0 10px 15px -3px rgba(255, 82, 82, 0.15)" 
                    }}
                    transition={{ type: "spring", stiffness: 400, damping: 10 }}
                  >
                    <ArrowLeft size={18} className="mr-2" />
                    Return Home
                  </motion.button>
                </Link>
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
} 